/**
 * CRM operations view — Phase 2.2.c.3 (CRM config / Porta plans).
 *
 * Owns the "CRM Installation Config" tab: a small admin screen that lists
 * the Porta product codes (a.k.a. subscription plans) configured for each
 * customer type, lets root/system_admin sync the catalogue from Porta,
 * and lets them add/disable a single product.
 *
 * What stays in admin.js (injected via `configure({helpers})`):
 *   - `escapeHtml`, `formatDateTimeShort`, `formatCrmLabel` (formatters).
 *   - `openDialog`, `closeDialog` (generic dialog helpers).
 *
 * Boot contract: `admin.html` loads this script after `crm.js` /
 * The view-router calls `init()` the first time the user opens the tab.
 *
 * Phase 3.2.c: replaced `configure({helpers})` injection with direct
 * imports from src/lib + cross-view import of formatCrmLabel from
 * the crm view (the canonical owner).
 */

import { escapeHtml, formatDateTimeShort } from '../../src/lib/format.js';
import { openDialog, closeDialog } from '../../src/lib/dialog.js';
import { formatCrmLabel } from './crm.js';
import * as quotesView from './crm-quotes.js';
import * as api from '../api-client.js';
import * as notify from '../notifications.js';

  // ---- Compat aliases (so lifted function bodies need zero changes) ----
  const adminAPI = api;
  const API = api.routes;
  const showFlash = (msg, type, ms) => notify.showFlash(msg, type, ms);

  // ---- DOM refs (lazy-resolved) -----------------------------------------
  let crmConfigCustomerType, crmConfigActiveOnly, crmConfigRefresh, crmConfigSync,
      crmConfigNewBtn, crmConfigTbody, crmConfigDialog, crmConfigForm,
      crmConfigType, crmConfigCode, crmConfigLabel, crmConfigPrice, crmConfigInstallFee, crmConfigActive,
      crmTierEntryMax, crmTierMidMax, crmTierCurrency, crmTierSaveBtn, crmTierStatus;

  let domResolved = false;
  function resolveDom() {
    if (domResolved) return;
    domResolved = true;
    crmConfigCustomerType = document.getElementById('crmConfigCustomerType');
    crmConfigActiveOnly = document.getElementById('crmConfigActiveOnly');
    crmConfigRefresh = document.getElementById('crmConfigRefresh');
    crmConfigSync = document.getElementById('crmConfigSync');
    crmConfigNewBtn = document.getElementById('crmConfigNewBtn');
    crmConfigTbody = document.getElementById('crmConfigTbody');
    crmConfigDialog = document.getElementById('crmConfigDlg');
    crmConfigForm = document.getElementById('crmConfigForm');
    crmConfigType = document.getElementById('crmConfigType');
    crmConfigCode = document.getElementById('crmConfigCode');
    crmConfigLabel = document.getElementById('crmConfigLabel');
    crmConfigPrice = document.getElementById('crmConfigPrice');
    crmConfigInstallFee = document.getElementById('crmConfigInstallFee');
    crmConfigActive = document.getElementById('crmConfigActive');
    crmTierEntryMax = document.getElementById('crmTierEntryMax');
    crmTierMidMax = document.getElementById('crmTierMidMax');
    crmTierCurrency = document.getElementById('crmTierCurrency');
    crmTierSaveBtn = document.getElementById('crmTierSaveBtn');
    crmTierStatus = document.getElementById('crmTierConfigStatus');
  }

  async function loadTierConfig() {
    if (!crmTierEntryMax) return;
    try {
      const cfg = await adminAPI.fetch(API.crmConfigPackageTiers, { method: 'GET' });
      crmTierEntryMax.value = cfg.entry_max_price ?? '';
      crmTierMidMax.value = cfg.mid_max_price ?? '';
      if (crmTierCurrency) crmTierCurrency.textContent = cfg.currency || 'GMD';
      if (crmTierStatus) crmTierStatus.textContent = '';
    } catch (err) {
      if (crmTierStatus) crmTierStatus.textContent = err?.message || 'Failed to load tiers';
    }
  }

  async function saveTierConfig() {
    if (!crmTierEntryMax) return;
    const entry = Number(crmTierEntryMax.value);
    const mid = Number(crmTierMidMax.value);
    if (!(entry >= 0) || !(mid >= 0) || mid < entry) {
      showFlash('Mid ceiling must be ≥ entry ceiling, both non-negative.', 'error');
      return;
    }
    if (crmTierSaveBtn) crmTierSaveBtn.disabled = true;
    try {
      await adminAPI.fetch(API.crmConfigPackageTiers, {
        method: 'PUT', body: JSON.stringify({ entry_max_price: entry, mid_max_price: mid }),
      });
      showFlash('Package tiers saved');
    } catch (err) {
      showFlash(err?.message || 'Failed to save tiers', 'error');
    } finally {
      if (crmTierSaveBtn) crmTierSaveBtn.disabled = false;
    }
  }

  // ---- State ------------------------------------------------------------
  const crmConfigState = {
    items: [],
    loading: false,
  };
  let crmConfigInitialized = false;

  // ---- Promotions catalog ----------------------------------------------
  let _promos = [];
  let _promoEditId = null;

  function _discountLabel(p) {
    if (p.discount_type === 'full_waiver') return 'Waive install fee';
    if (p.discount_type === 'amount') return `${p.value ?? 0} off install`;
    if (p.discount_type === 'percent') return `${p.value ?? 0}% off install`;
    return p.discount_type || '';
  }

  async function loadPromotions() {
    const tb = document.getElementById('crmPromoTbody');
    if (!tb) return;
    tb.innerHTML = '<tr><td colspan="7" style="color:var(--sub);">Loading…</td></tr>';
    try {
      const data = await adminAPI.fetch(API.crmPromotions(), { method: 'GET' });
      _promos = Array.isArray(data?.items) ? data.items : [];
      if (!_promos.length) {
        tb.innerHTML = '<tr><td colspan="7" style="color:var(--sub);">No promotions yet.</td></tr>';
        return;
      }
      tb.innerHTML = _promos.map((p, i) => {
        const elig = (p.eligible_customer_types && p.eligible_customer_types.length)
          ? p.eligible_customer_types.map((c) => formatCrmLabel(c)).join(', ') : 'All';
        return `<tr>
          <td>${i + 1}</td>
          <td>${escapeHtml(p.code)}</td>
          <td>${escapeHtml(p.name)}</td>
          <td>${escapeHtml(_discountLabel(p))}</td>
          <td>${escapeHtml(elig)}</td>
          <td>${p.is_active ? 'Yes' : 'No'}</td>
          <td>
            <button class="btn xs" data-promo-edit="${escapeHtml(p.id)}">Edit</button>
            <button class="btn xs" data-promo-toggle="${escapeHtml(p.id)}">${p.is_active ? 'Disable' : 'Enable'}</button>
          </td>
        </tr>`;
      }).join('');
    } catch (err) {
      tb.innerHTML = `<tr><td colspan="7" style="color:var(--err);">${escapeHtml(err?.message || 'Failed to load')}</td></tr>`;
    }
  }

  function _syncPromoValueVisibility() {
    const type = document.getElementById('crmPromoType');
    const wrap = document.getElementById('crmPromoValueWrap');
    if (type && wrap) wrap.style.display = type.value === 'full_waiver' ? 'none' : '';
  }

  function openPromoDialog(promo) {
    const dlg = document.getElementById('crmPromoDlg');
    const form = document.getElementById('crmPromoForm');
    if (!dlg || !form) return;
    form.reset();
    _promoEditId = promo ? promo.id : null;
    const title = document.getElementById('crmPromoDlgTitle');
    if (title) title.textContent = promo ? 'Edit promotion' : 'Add promotion';
    const code = document.getElementById('crmPromoCode');
    if (code) code.disabled = !!promo;  // code is the immutable key
    if (promo) {
      code.value = promo.code || '';
      document.getElementById('crmPromoName').value = promo.name || '';
      document.getElementById('crmPromoDesc').value = promo.description || '';
      document.getElementById('crmPromoType').value = promo.discount_type || 'full_waiver';
      document.getElementById('crmPromoValue').value = promo.value ?? '';
      document.getElementById('crmPromoActive').checked = !!promo.is_active;
      const elig = new Set(promo.eligible_customer_types || []);
      document.querySelectorAll('[data-promo-ct]').forEach((cb) => { cb.checked = elig.has(cb.dataset.promoCt); });
    } else {
      document.getElementById('crmPromoActive').checked = true;
    }
    _syncPromoValueVisibility();
    openDialog(dlg);
  }

  async function submitPromo(event) {
    event.preventDefault();
    const type = document.getElementById('crmPromoType').value;
    const valRaw = document.getElementById('crmPromoValue').value.trim();
    const eligible = Array.from(document.querySelectorAll('[data-promo-ct]:checked')).map((cb) => cb.dataset.promoCt);
    const base = {
      name: document.getElementById('crmPromoName').value.trim(),
      description: document.getElementById('crmPromoDesc').value.trim() || null,
      discount_type: type,
      value: type === 'full_waiver' ? null : (valRaw ? Number(valRaw) : null),
      eligible_customer_types: eligible,
      is_active: document.getElementById('crmPromoActive').checked,
    };
    try {
      if (_promoEditId) {
        await adminAPI.fetch(API.crmPromotion(_promoEditId), { method: 'PATCH', body: JSON.stringify(base) });
      } else {
        await adminAPI.fetch(API.crmPromotions(), {
          method: 'POST',
          body: JSON.stringify({ ...base, code: document.getElementById('crmPromoCode').value.trim() }),
        });
      }
      showFlash('Promotion saved');
      closeDialog(document.getElementById('crmPromoDlg'));
      loadPromotions();
    } catch (err) {
      showFlash(err?.message || 'Failed to save promotion', 'error');
    }
  }

  async function loadCrmConfigProducts() {
    if (!crmConfigTbody || crmConfigState.loading) return;
    crmConfigState.loading = true;
    crmConfigTbody.innerHTML = '<tr><td colspan="8" style="color:var(--sub);">Loading…</td></tr>';
    try {
      const params = new URLSearchParams();
      if (crmConfigCustomerType && crmConfigCustomerType.value) params.set('customer_type', crmConfigCustomerType.value);
      if (crmConfigActiveOnly && crmConfigActiveOnly.checked) params.set('active_only', 'true');
      const data = await adminAPI.fetch(API.crmConfigProducts(params.toString()), { method: 'GET' });
      const items = Array.isArray(data?.items) ? data.items : [];
      crmConfigState.items = items;
      renderCrmConfigProducts(items);
    } catch (err) {
      crmConfigTbody.innerHTML = '<tr><td colspan="8" style="color:var(--err);">Failed to load config</td></tr>';
      showFlash(err?.message || 'Failed to load config', 'error');
    } finally {
      crmConfigState.loading = false;
    }
  }

  function renderCrmConfigProducts(items) {
    if (!crmConfigTbody) return;
    crmConfigTbody.innerHTML = '';
    if (!items.length) {
      crmConfigTbody.innerHTML = '<tr><td colspan="8" style="color:var(--sub);">No products configured.</td></tr>';
      return;
    }
    items.forEach((item, index) => {
      const tr = document.createElement('tr');
      const price = item.monthly_price != null ? item.monthly_price : '';
      const fee = item.install_fee != null ? item.install_fee : '';
      tr.innerHTML = `
        <td>${index + 1}</td>
        <td>${escapeHtml(item.porta_product_code || '')}</td>
        <td>${escapeHtml(formatCrmLabel(item.customer_type || ''))}</td>
        <td>${escapeHtml(item.label || '—')}</td>
        <td>
          <div class="row" style="gap:6px; align-items:center;">
            <input type="number" min="0" step="0.01" value="${escapeHtml(String(price))}" data-price-input="${escapeHtml(item.id)}" style="width:90px;" title="Monthly price" placeholder="mo" />
            <input type="number" min="0" step="0.01" value="${escapeHtml(String(fee))}" data-fee-input="${escapeHtml(item.id)}" style="width:90px;" title="Installation fee" placeholder="install" />
            <button class="btn xs" data-price-save="${escapeHtml(item.id)}">Save</button>
          </div>
        </td>
        <td>${item.is_active ? 'Yes' : 'No'}</td>
        <td>${item.updated_at ? formatDateTimeShort(item.updated_at) : ''}</td>
        <td>
          <button class="btn" data-config-toggle="${escapeHtml(item.id)}">${item.is_active ? 'Disable' : 'Enable'}</button>
        </td>
      `;
      crmConfigTbody.appendChild(tr);
    });
  }

  function initCrmConfigView() {
    resolveDom();
    if (crmConfigInitialized) {
      loadTierConfig();
      loadPromotions();
      loadCrmConfigProducts();
      quotesView.mountConfig();
      return;
    }
    crmConfigInitialized = true;
    if (crmConfigRefresh) {
      crmConfigRefresh.addEventListener('click', () => loadCrmConfigProducts());
    }
    if (crmConfigSync) {
      crmConfigSync.addEventListener('click', async () => {
        if (crmConfigSync) crmConfigSync.disabled = true;
        try {
          const params = new URLSearchParams();
          if (crmConfigCustomerType && crmConfigCustomerType.value) {
            params.set('customer_type', crmConfigCustomerType.value);
          }
          const data = await adminAPI.fetch(API.crmConfigSync(params.toString()), { method: 'POST' });
          const items = Array.isArray(data?.items) ? data.items : [];
          const created = items.reduce((sum, item) => sum + (item.created || 0), 0);
          const updated = items.reduce((sum, item) => sum + (item.updated || 0), 0);
          const deactivated = items.reduce((sum, item) => sum + (item.deactivated || 0), 0);
          showFlash(`Sync complete. ${created} created, ${updated} updated, ${deactivated} deactivated.`);
          loadCrmConfigProducts();
        } catch (err) {
          showFlash(err?.message || 'Failed to sync products', 'error');
        } finally {
          if (crmConfigSync) crmConfigSync.disabled = false;
        }
      });
    }
    if (crmConfigCustomerType) {
      crmConfigCustomerType.addEventListener('change', () => loadCrmConfigProducts());
    }
    if (crmConfigActiveOnly) {
      crmConfigActiveOnly.addEventListener('change', () => loadCrmConfigProducts());
    }
    if (crmConfigNewBtn) {
      crmConfigNewBtn.addEventListener('click', () => {
        if (crmConfigForm) crmConfigForm.reset();
        openDialog(crmConfigDialog);
      });
    }
    if (crmConfigForm) {
      crmConfigForm.addEventListener('submit', async (event) => {
        event.preventDefault();
        const priceRaw = crmConfigPrice && crmConfigPrice.value.trim();
        const feeRaw = crmConfigInstallFee && crmConfigInstallFee.value.trim();
        const payload = {
          customer_type: crmConfigType.value,
          porta_product_code: crmConfigCode.value.trim(),
          label: crmConfigLabel.value.trim() || null,
          monthly_price: priceRaw ? Number(priceRaw) : null,
          install_fee: feeRaw ? Number(feeRaw) : null,
          is_active: crmConfigActive.checked,
        };
        if (!payload.porta_product_code) {
          showFlash('Porta product code is required', 'error');
          return;
        }
        try {
          await adminAPI.fetch(API.crmConfigProducts(), { method: 'POST', body: JSON.stringify(payload) });
          showFlash('Product added');
          closeDialog(crmConfigDialog);
          loadCrmConfigProducts();
        } catch (err) {
          showFlash(err?.message || 'Failed to add product', 'error');
        }
      });
    }
    if (crmConfigTbody) {
      crmConfigTbody.addEventListener('click', async (event) => {
        const saveBtn = event.target.closest('button[data-price-save]');
        if (saveBtn) {
          const id = saveBtn.dataset.priceSave;
          const priceEl = crmConfigTbody.querySelector(`input[data-price-input="${CSS.escape(id)}"]`);
          const feeEl = crmConfigTbody.querySelector(`input[data-fee-input="${CSS.escape(id)}"]`);
          const priceRaw = priceEl ? priceEl.value.trim() : '';
          const feeRaw = feeEl ? feeEl.value.trim() : '';
          try {
            await adminAPI.fetch(API.crmConfigProduct(id), {
              method: 'PATCH',
              body: JSON.stringify({
                monthly_price: priceRaw ? Number(priceRaw) : null,
                install_fee: feeRaw ? Number(feeRaw) : null,
              }),
            });
            showFlash('Pricing saved');
          } catch (err) {
            showFlash(err?.message || 'Failed to save pricing', 'error');
          }
          return;
        }
        const btn = event.target.closest('button[data-config-toggle]');
        if (!btn) return;
        const id = btn.dataset.configToggle;
        const config = crmConfigState.items.find((item) => String(item.id) === String(id));
        if (!config) return;
        try {
          await adminAPI.fetch(API.crmConfigProduct(id), { method: 'PATCH', body: JSON.stringify({ is_active: !config.is_active }) });
          showFlash('Updated');
          loadCrmConfigProducts();
        } catch (err) {
          showFlash(err?.message || 'Failed to update', 'error');
        }
      });
    }
    if (crmTierSaveBtn) crmTierSaveBtn.addEventListener('click', saveTierConfig);

    const promoNewBtn = document.getElementById('crmPromoNewBtn');
    if (promoNewBtn) promoNewBtn.addEventListener('click', () => openPromoDialog(null));
    const promoForm = document.getElementById('crmPromoForm');
    if (promoForm) promoForm.addEventListener('submit', submitPromo);
    const promoType = document.getElementById('crmPromoType');
    if (promoType) promoType.addEventListener('change', _syncPromoValueVisibility);
    const promoTbody = document.getElementById('crmPromoTbody');
    if (promoTbody) {
      promoTbody.addEventListener('click', async (event) => {
        const editBtn = event.target.closest('button[data-promo-edit]');
        if (editBtn) {
          const p = _promos.find((x) => String(x.id) === String(editBtn.dataset.promoEdit));
          if (p) openPromoDialog(p);
          return;
        }
        const tog = event.target.closest('button[data-promo-toggle]');
        if (tog) {
          const p = _promos.find((x) => String(x.id) === String(tog.dataset.promoToggle));
          if (!p) return;
          try {
            await adminAPI.fetch(API.crmPromotion(p.id), { method: 'PATCH', body: JSON.stringify({ is_active: !p.is_active }) });
            loadPromotions();
          } catch (err) {
            showFlash(err?.message || 'Failed to update promotion', 'error');
          }
        }
      });
    }

    loadTierConfig();
    loadPromotions();
    loadCrmConfigProducts();
    quotesView.mountConfig();
  }

export default {
  init: initCrmConfigView,
  getState: () => crmConfigState,
};
